import { DiagramModel } from "@projectstorm/react-diagrams";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { v4 as uuidv4 } from "uuid";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector, useStore } from "react-redux";
import MainWindow from "./MainWindow";
import Context from "../store/context";
import { proxyAPI } from "../proxyAPI";
import {
  addNewWorkflow,
  closeProject,
  createNewMessageConfig,
  editAction,
  saveProject,
  selectProjectUUID,
  selectWorkflows,
  setProjectData,
} from "../store/slices/projectSlice";
import { setSendLists } from "../store/slices/sendListsSlice";
import { createEditorState, makeid, shortUUID } from "../helpers";
import { setSurveyThemes } from "../store/slices/surveyThemesSlice";
import FullScreenLoadingIndicator from "../Common/FullScreenLoadingIndicator";
import { useGetContactFieldsQuery } from "../store/slices/contactsSlice";
import {
  useGetCommsSettingsDataQuery,
  useGetSystemDataStructuresQuery,
} from "../store/slices/settingsSlice";

// page props
const { productName } = JSON.parse(
  document.querySelector("#app").dataset.page
).props;

// single workflow
const newWorkflow = (IsEntrypoint = false) => ({
  Label: "New workflow",
  UUID: uuidv4(),
  IsEntrypoint,
  EventListeners: [],
});

const getActionUuidFromPortName = (portName) => {
  if (!portName) return null;
  // check pipe
  if (portName.includes("|")) {
    return portName.split("|")[1];
  }
  // check length for old versions - both have long uuid
  if (portName.length === 73) {
    return portName.slice(37);
  }
  // if 71 both from backend - both have short uuid
  if (portName.length === 71) {
    return portName.slice(36);
  }
  //if 72 check last two character
  if (portName.length === 72) {
    if (portName.slice(-2) === "-s") {
      // second part is short uuid with -s at the end
      return portName.slice(37);
    } else {
      // first part is short
      return portName.slice(36);
    }
  }
};

export default function AdvancedBuilder({ engine, dagreEngine }) {
  const { data: commsSettings } = useGetCommsSettingsDataQuery();

  // new project
  const newProjectData = () => {
    const newMessageConfigUUID = uuidv4();

    return {
      Label: "New Project",
      UUID: shortUUID(),
      Status: "draft",
      Reference: makeid(8),
      Workflows: [newWorkflow(true)],
      EventHandlers: [],
      BusinessHours: [],
      MessageTemplates: [],
      InputArguments: [],
      MessageConfigUUID: newMessageConfigUUID,
      MessageConfigs: [
        createNewMessageConfig({
          UUID: newMessageConfigUUID,
          Label: "Default message config",
          commsSettings,
        }),
      ],
    };
  };

  // inertia props
  const params = useParams();

  const dispatch = useDispatch();
  const store = useStore();
  const navigate = useNavigate();
  const zoomedInNode = useRef(null);

  // global state (to be passed in context)
  const {
    data: contactFields = [],
    isFetching,
    isLoading,
  } = useGetContactFieldsQuery();
  const [reportingTopics, setreportingTopics] = useState([]);
  const [reportingTemplates, setReportingTemplates] = useState([]);
  const [apiProfiles, setApiProfiles] = useState([]);
  const [components, setComponents] = useState([]);
  const [jsonCollections, setJsonCollections] = useState([]);
  const [isModalSidebar, setIsModalSidebar] = useState(false);

  // workflows data
  const projectUUID = useSelector(selectProjectUUID);
  const [projectLoading, setProjectLoading] = useState(false);

  const save = useCallback(
    () => dispatch(saveProject({ navigate, engine })),
    [dispatch, engine, navigate]
  );

  // zoom to fit handler
  const zoomToFit = useCallback(() => {
    engine.zoomToFitNodes({ margin: 10, maxZoom: 0.8 });
  }, [engine]);

  const rerouteLinksAroundNodes = useCallback(() => {
    const model = engine.model;
    const nodes = model.getNodes ? model.getNodes() : [];
    const links = model.getLinks ? Object.values(model.getLinks()) : [];

    if (!nodes.length || !links.length) {
      return;
    }

    const nodeRects = nodes.map((n) => {
      const x = n.getX ? n.getX() : n.position?.x || 0;
      const y = n.getY ? n.getY() : n.position?.y || 0;
      const width = n.width || n.options?.width || 0;
      const height = n.height || n.options?.height || 0;
      return {
        id: n.getID?.() || n.options?.id,
        left: x,
        right: x + width,
        top: y,
        bottom: y + height,
      };
    });

    const horizontalLaneIsClear = (x1, x2, y, ignoredNodeIds) => {
      const minX = Math.min(x1, x2);
      const maxX = Math.max(x1, x2);
      const yPadding = 18;
      const xPadding = 12;

      return !nodeRects.some((rect) => {
        if (ignoredNodeIds.has(rect.id)) {
          return false;
        }
        const overlapsX = maxX >= rect.left - xPadding && minX <= rect.right + xPadding;
        const overlapsY = y >= rect.top - yPadding && y <= rect.bottom + yPadding;
        return overlapsX && overlapsY;
      });
    };

    const verticalLaneIsClear = (x, y1, y2, ignoredNodeIds) => {
      const minY = Math.min(y1, y2);
      const maxY = Math.max(y1, y2);
      const xPadding = 18;
      const yPadding = 12;

      return !nodeRects.some((rect) => {
        if (ignoredNodeIds.has(rect.id)) {
          return false;
        }
        const overlapsX = x >= rect.left - xPadding && x <= rect.right + xPadding;
        const overlapsY = maxY >= rect.top - yPadding && minY <= rect.bottom + yPadding;
        return overlapsX && overlapsY;
      });
    };

    // Track occupied routed segments so parallel links can be spread in both
    // the horizontal lane and the source/target bend columns.
    const occupiedRoutes = [];

    [...links]
      .sort((a, b) => {
        const aPoints = a.getPoints?.() || [];
        const bPoints = b.getPoints?.() || [];
        const aSpan =
          aPoints.length > 1
            ? Math.abs(
                (aPoints[aPoints.length - 1].getPosition?.().x || 0) -
                  (aPoints[0].getPosition?.().x || 0)
              )
            : 0;
        const bSpan =
          bPoints.length > 1
            ? Math.abs(
                (bPoints[bPoints.length - 1].getPosition?.().x || 0) -
                  (bPoints[0].getPosition?.().x || 0)
              )
            : 0;
        return bSpan - aSpan;
      })
      .forEach((link) => {
      const points = link.getPoints?.() || [];
      const sourcePort = link.getSourcePort?.();
      const targetPort = link.getTargetPort?.();

      if (!sourcePort || !targetPort || points.length < 2) {
        return;
      }

      const firstPoint = points[0];
      const lastPoint = points[points.length - 1];

      const sourcePos = firstPoint.getPosition?.();
      const targetPos = lastPoint.getPosition?.();

      if (!sourcePos || !targetPos) {
        return;
      }

      const sourceNodeId = sourcePort.getNode?.()?.getID?.();
      const targetNodeId = targetPort.getNode?.()?.getID?.();
      const ignoredNodeIds = new Set([sourceNodeId, targetNodeId]);

      const direction = sourcePos.x <= targetPos.x ? 1 : -1;
      const diagonalOffset = 26;
      const span = Math.abs(targetPos.x - sourcePos.x);
      const laneStep = 60;
      const laneSearchDepth = 12;
      const midLane = (sourcePos.y + targetPos.y) / 2;

      const candidateLanes = [midLane];
      for (let i = 1; i <= laneSearchDepth; i += 1) {
        candidateLanes.push(midLane - laneStep * i);
        candidateLanes.push(midLane + laneStep * i);
      }

      const straightLaneClear =
        span > 0 &&
        horizontalLaneIsClear(
          sourcePos.x,
          targetPos.x,
          sourcePos.y,
          ignoredNodeIds
        ) &&
        horizontalLaneIsClear(
          sourcePos.x,
          targetPos.x,
          targetPos.y,
          ignoredNodeIds
        );

      if (straightLaneClear && span < 260) {
        return;
      }

      const laneCongestionScore = (laneY, segmentMinX, segmentMaxX) => {
        let score = 0;
        occupiedRoutes.forEach((segment) => {
          const overlapsX =
            segmentMaxX >= segment.minX - 8 && segmentMinX <= segment.maxX + 8;
          if (!overlapsX) {
            return;
          }

          const laneDistance = Math.abs(laneY - segment.laneY);
          if (laneDistance < laneStep * 0.4) {
            score += 12;
          } else if (laneDistance < laneStep * 0.9) {
            score += 4;
          }
        });
        return score;
      };

      const columnCongestionScore = (
        sourceBendX,
        targetBendX,
        sourceLaneY,
        targetLaneY
      ) => {
        let score = 0;

        occupiedRoutes.forEach((route) => {
          const sourceOverlapY =
            Math.max(sourcePos.y, sourceLaneY) >=
              Math.min(route.sourceMinY, route.sourceMaxY) - 8 &&
            Math.min(sourcePos.y, sourceLaneY) <=
              Math.max(route.sourceMinY, route.sourceMaxY) + 8;
          if (sourceOverlapY) {
            const sourceColumnDistance = Math.abs(sourceBendX - route.sourceBendX);
            if (sourceColumnDistance < 10) {
              score += 10;
            } else if (sourceColumnDistance < 24) {
              score += 4;
            }
          }

          const targetOverlapY =
            Math.max(targetPos.y, targetLaneY) >=
              Math.min(route.targetMinY, route.targetMaxY) - 8 &&
            Math.min(targetPos.y, targetLaneY) <=
              Math.max(route.targetMinY, route.targetMaxY) + 8;
          if (targetOverlapY) {
            const targetColumnDistance = Math.abs(targetBendX - route.targetBendX);
            if (targetColumnDistance < 10) {
              score += 10;
            } else if (targetColumnDistance < 24) {
              score += 4;
            }
          }
        });

        return score;
      };

      const maxOffsetSteps = Math.max(
        1,
        Math.min(5, Math.floor(Math.max(span - diagonalOffset * 2, 0) / 48))
      );

      let chosenLaneY = (sourcePos.y + targetPos.y) / 2;
      let chosenSourceBendX = sourcePos.x + diagonalOffset * direction;
      let chosenTargetBendX = targetPos.x - diagonalOffset * direction;
      let bestScore = Number.POSITIVE_INFINITY;

      for (let offsetStep = 1; offsetStep <= maxOffsetSteps; offsetStep += 1) {
        const bendOffset = diagonalOffset + (offsetStep - 1) * 18;
        const sourceBendX = sourcePos.x + bendOffset * direction;
        const targetBendX = targetPos.x - bendOffset * direction;
        const segmentMinX = Math.min(sourceBendX, targetBendX);
        const segmentMaxX = Math.max(sourceBendX, targetBendX);

        const isLaneValid = (laneY) =>
          horizontalLaneIsClear(
            sourceBendX,
            targetBendX,
            laneY,
            ignoredNodeIds
          ) &&
          verticalLaneIsClear(sourceBendX, sourcePos.y, laneY, ignoredNodeIds) &&
          verticalLaneIsClear(targetBendX, targetPos.y, laneY, ignoredNodeIds);

        candidateLanes.forEach((laneY) => {
          if (!isLaneValid(laneY)) {
            return;
          }

          const distanceScore = Math.abs(laneY - midLane) / laneStep;
          const congestionScore = laneCongestionScore(
            laneY,
            segmentMinX,
            segmentMaxX
          );
          const columnScore = columnCongestionScore(
            sourceBendX,
            targetBendX,
            laneY,
            laneY
          );
          const offsetScore = (bendOffset - diagonalOffset) / 18;
          const totalScore =
            distanceScore + congestionScore + columnScore + offsetScore * 0.5;

          if (totalScore < bestScore) {
            bestScore = totalScore;
            chosenLaneY = laneY;
            chosenSourceBendX = sourceBendX;
            chosenTargetBendX = targetBendX;
          }
        });
      }

      const sourceVerticalDelta = chosenLaneY - sourcePos.y;
      const targetVerticalDelta = chosenLaneY - targetPos.y;

      const sourceDiagY =
        sourcePos.y +
        (sourceVerticalDelta === 0
          ? 0
          : Math.sign(sourceVerticalDelta) *
            Math.min(diagonalOffset, Math.abs(sourceVerticalDelta)));

      const targetDiagY =
        targetPos.y +
        (targetVerticalDelta === 0
          ? 0
          : Math.sign(targetVerticalDelta) *
            Math.min(diagonalOffset, Math.abs(targetVerticalDelta)));

      const PointCtor = firstPoint.constructor;
      const buildPoint = (x, y) => new PointCtor({ link, position: { x, y } });

      const newPoints = [
        firstPoint,
        buildPoint(chosenSourceBendX, sourceDiagY),
        buildPoint(chosenSourceBendX, chosenLaneY),
        buildPoint(chosenTargetBendX, chosenLaneY),
        buildPoint(chosenTargetBendX, targetDiagY),
        lastPoint,
      ];

      if (typeof link.setPoints === "function") {
        link.setPoints(newPoints);
      }

      occupiedRoutes.push({
        minX: Math.min(chosenSourceBendX, chosenTargetBendX),
        maxX: Math.max(chosenSourceBendX, chosenTargetBendX),
        laneY: chosenLaneY,
        sourceBendX: chosenSourceBendX,
        targetBendX: chosenTargetBendX,
        sourceMinY: Math.min(sourcePos.y, chosenLaneY),
        sourceMaxY: Math.max(sourcePos.y, chosenLaneY),
        targetMinY: Math.min(targetPos.y, chosenLaneY),
        targetMaxY: Math.max(targetPos.y, chosenLaneY),
      });
    });

    engine.repaintCanvas();
  }, [engine]);

  // autoarrange handler
  const autoArrange = useCallback(() => {
    dagreEngine.redistribute(engine.model);
    rerouteLinksAroundNodes();
    zoomToFit();
  }, [dagreEngine, engine, rerouteLinksAroundNodes, zoomToFit]);

  const registerDiagramLinkListener = useCallback(
    (model) => {
      model.registerListener({
        linksUpdated: (event) => {
          event.link.registerListener({
            targetPortChanged: (e) => {
              const targetUUID =
                e.entity.targetPort?.parent?.options?.id ?? null;
              const actionUUID = getActionUuidFromPortName(
                e.entity.sourcePort?.options?.name
              );

              if (!actionUUID) {
                return;
              }

              dispatch(
                editAction({
                  actionUUID,
                  field: "TypeUUID",
                  value: targetUUID,
                })
              );
            },
          });
        },
      });
    },
    [dispatch]
  );

  // add a child node to a parent node
  // if no parent, add the node to the canvas
  const addChild = useCallback(
    (data, parentUUID) => {
      const childData = data ?? { workflow: newWorkflow() };

      dispatch(addNewWorkflow(childData.workflow));
      // create the node model using the factory
      const newNode = engine.nodeFactories
        .getFactory("logic")
        .generateModel(childData);
      // add the node to the model
      engine.model.addNode(newNode);
      // if there is a parent node, create a link
      newNode.setSelected();
      if (parentUUID) {
        const parentNode = engine.model.getNode(parentUUID);
        newNode.setPosition(
          parentNode.position.x + 1000,
          parentNode.position.y
        );
      }
      // show the available nodes that can connect the new node
      engine.model.clearSelection();
      engine.repaintCanvas();
      setTimeout(() => {
        zoomToFit();
      }, 100);
      return newNode;
    },
    [dispatch, engine, zoomToFit]
  );

  // initialize project data
  const initializeWorkflowNodes = useCallback(
    (workflows) => {
      if (workflows.length > 0) {
        const model = new DiagramModel();
        registerDiagramLinkListener(model);
        engine.setModel(model);
        setTimeout(() => {
          workflows.forEach((workflow) =>
            addChild({
              workflow,
            })
          );
          setTimeout(() => {
            autoArrange();
          }, 200);
        }, 100);
      }
    },
    [addChild, autoArrange, engine, registerDiagramLinkListener]
  );

  const resetLayout = useCallback(() => {
    const projectWorkflows = selectWorkflows(store.getState());
    initializeWorkflowNodes(projectWorkflows);
  }, [initializeWorkflowNodes, store]);

  const zoomToNode = useCallback(
    (node) => {
      if (!node) {
        return;
      }

      const canvas = engine.getCanvas();
      const nodeBounds = node.getBoundingBox();
      const topLeft = nodeBounds.getTopLeft();
      const zoom = 0.8;
      const nodeCenterX = topLeft.x + nodeBounds.getWidth() / 2;

      engine.model.setZoomLevel(80);
      engine.model.setOffset(
        canvas.clientWidth / 2 - nodeCenterX * zoom,
        10 - topLeft.y * zoom
      );
      engine.repaintCanvas();
    },
    [engine]
  );

  // double click to zoom handler
  const zoomToSelected = useCallback(() => {
    const [selectedNode] = engine.model.getSelectedEntities();

    if (!selectedNode) {
      engine.zoomToFitNodes({ margin: 10, maxZoom: 0.8 });
      zoomedInNode.current = null;
      return;
    }

    if (
      zoomedInNode.current === null ||
      zoomedInNode.current !== selectedNode
    ) {
      zoomToNode(selectedNode);
      zoomedInNode.current = selectedNode;
    } else {
      engine.zoomToFitNodes({ margin: 10, maxZoom: 0.8 });
      zoomedInNode.current = null;
    }
  }, [engine, zoomToNode]);

  // project loader
  const projectLoader = useCallback(async () => {
    setProjectLoading(true);
    let projectData = null;

    if (params.projectID === undefined) {
      setProjectLoading(false);
      return null;
    }

    // new project
    if (params.projectID === "new") {
      if (commsSettings) {
        projectData = newProjectData();
      }
    } else {
      // get project data
      projectData = await proxyAPI({
        prefix: "logic/workflows/api",
        url: `/project/${params.projectID}`,
        setLoading: () => null,
      });
    }

    // get themes
    const themes = await proxyAPI({
      prefix: "logic/workflows/api",
      url: `/themes`,
      setLoading: () => null,
    });

    // get send lists for templates and workflows
    const sendLists = await Promise.all(
      [
        ...(projectData?.MessageTemplates ?? []),
        ...(projectData?.Workflows || []),
      ]
        .filter((mt) => mt.ContactSetUUID === "yes")
        .map((mt) =>
          proxyAPI({ prefix: "contacts/v1", url: `/send-list/${mt.UUID}` })
        )
    ).then((values) => values.filter((v) => v));
    setTimeout(() => {
      setProjectLoading(false);
    }, 400);
    return { projectData, themes, sendLists };
  }, [commsSettings, params.projectID]);

  // load project
  useEffect(() => {
    if (!params.projectID) {
      dispatch(closeProject());
      return;
    }
    if (params.projectID === "new" && !commsSettings) {
      return;
    }
    if (params.projectID) {
      let isCurrentLoad = true;
      (async () => {
        const loaderData = await projectLoader();

        if (!isCurrentLoad || !loaderData?.projectData) {
          setProjectLoading(false);
          return;
        }

        dispatch(setProjectData(loaderData.projectData));
        dispatch(setSendLists(loaderData.sendLists));
        dispatch(setSurveyThemes(loaderData.themes));
        if (loaderData.projectData.ComponentConfig?.Layout) {
          const newModel = new DiagramModel();
          newModel.deserializeModel(
            loaderData.projectData.ComponentConfig.Layout,
            engine
          );
          registerDiagramLinkListener(newModel);
          engine.setModel(newModel);
          requestAnimationFrame(() => {
            engine.repaintCanvas();
          });
        } else {
          initializeWorkflowNodes(loaderData.projectData.Workflows);
        }
      })();
      return () => {
        isCurrentLoad = false;
        dispatch(closeProject());
      };
    }
  }, [
    commsSettings,
    dispatch,
    engine,
    initializeWorkflowNodes,
    params.projectID,
    projectLoader,
    registerDiagramLinkListener,
  ]);

  // update contactfields
  useEffect(() => {
    if (params.workflowUUID) {
      proxyAPI({
        prefix: "logic/reporting",
        url: "/topics?pagination.perPage=1000",
      }).then((data) => setreportingTopics(data.data));
      proxyAPI({
        prefix: "logic/reporting",
        url: "/templates?pagination.perPage=1000",
      }).then((data) => setReportingTemplates(data.data));
      proxyAPI({
        prefix: "logic/workflows/api",
        url: `api-profiles`,
      }).then((data) => {
        if (data?.profiles) {
          setApiProfiles(data.profiles);
        }
      });
      proxyAPI({
        prefix: "logic/workflows/api",
        url: `json-api-data-collections`,
      }).then((data) => {
        if (data?.collections) {
          setJsonCollections(data.collections);
        }
      });
      proxyAPI({
        prefix: "logic/workflows/api",
        url: `list-projects?withParams=true&offset=0&limit=100`,
      }).then((data) => {
        if (data?.projects) {
          setComponents(data.projects);
        }
      });
    }
  }, [params.workflowUUID]);

  const { data: systemDataStructures } = useGetSystemDataStructuresQuery();

  const contextValue = useMemo(
    () => ({
      addChild,
      engine,
      isModalSidebar,
      setIsModalSidebar,
      saveProject: save,
      zoomToNode,
      contactFields,
      reportingTopics,
      reportingTemplates,
      apiProfiles,
      jsonCollections,
      systemDataStructures,
      components,
    }),
    [
      addChild,
      apiProfiles,
      components,
      contactFields,
      engine,
      isModalSidebar,
      jsonCollections,
      reportingTemplates,
      reportingTopics,
      save,
      systemDataStructures,
      zoomToNode,
    ]
  );

  return (
    <>
      <FullScreenLoadingIndicator loading={projectLoading} />
      {projectUUID ? (
        <Context.Provider value={contextValue}>
          <MainWindow
            addChild={addChild}
            engine={engine}
            redistribute={autoArrange}
            saveProject={save}
            zoomToFit={zoomToFit}
            zoomToSelected={zoomToSelected}
            resetLayout={resetLayout}
          />
        </Context.Provider>
      ) : (
        <div className="alert alert-warning text-center container my-5">
          Project not found.{" "}
          <Link to="/library/logic/builder/new/settings">Create new</Link>
        </div>
      )}
    </>
  );
}
